import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { RevenueEntriesApi } from 'api/revenue-entries/revenue-entries/api';
import type {
  RevenueEntryAccess,
  RevenueEntryAccessFlags,
} from 'api/revenue-entries/revenue-entries/types';
import { GENERAL_API_STALE } from 'config/query-config';

const REVENUE_ENTRIES_ACCESS_KEY = 'revenue-entries-access';
const ACCESSIBLE_PROJECTS_QUERY_KEY = [
  REVENUE_ENTRIES_ACCESS_KEY,
  'project-access',
];
const ACCESS_FLAGS_ORDER_QUERY_KEY = [
  REVENUE_ENTRIES_ACCESS_KEY,
  'access-flags-order',
];

const ACCESS_QUERY_KEY = (projectId?: number) => [
  REVENUE_ENTRIES_ACCESS_KEY,
  'access',
  projectId,
];

/**
 * Hook to get accessible project IDs for creating revenue entries
 */
export const useGetAccessibleRevenueProjects = () => {
  return useQuery({
    queryKey: ACCESSIBLE_PROJECTS_QUERY_KEY,
    queryFn: () => RevenueEntriesApi.getAccessibleProjects('CREATE'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get revenue entry access flags order/mapping
 */
export const useRevenueEntryAccessFlagsOrder = () => {
  return useQuery<Record<RevenueEntryAccessFlags, number>>({
    queryKey: ACCESS_FLAGS_ORDER_QUERY_KEY,
    queryFn: () => RevenueEntriesApi.getRevenueEntryAccessFlagsOrder(),
    staleTime: GENERAL_API_STALE,
  });
};

/**
 * Hook to get revenue entry access (account-level or project-specific)
 */
export const useGetRevenueEntryAccess = (
  projectId?: number,
  queryOptions?: UseQueryOptions<
    RevenueEntryAccess,
    unknown,
    RevenueEntryAccess,
    (string | number | undefined)[]
  >,
) => {
  return useQuery<
    RevenueEntryAccess,
    unknown,
    RevenueEntryAccess,
    (string | number | undefined)[]
  >({
    queryKey: ACCESS_QUERY_KEY(projectId),
    queryFn: () => RevenueEntriesApi.getRevenueEntryAccess(projectId),
    staleTime: GENERAL_API_STALE,
    ...(queryOptions ?? {}),
  });
};
